import React, { useState, useEffect } from 'react';
import { Plus, Users, Edit2, Trash2, UserPlus, UserMinus, X, Search, CheckCircle } from 'lucide-react';
import useSuperAdminMyTeamStore from '@/stores/SuperAdminStore/SuperAdminMyTeamStore';
import { Team, AvailableUser } from '@/services/PagesServices/SuperAdminMyTeamService';
import './ManageTeamsTab.css';

const ManageTeamsTab: React.FC = () => {
  const {
    teams,
    selectedTeam,
    teamMembers,
    availableUsers,
    loading,
    error,
    fetchAllTeams,
    fetchTeamById,
    createNewTeam,
    updateExistingTeam,
    deleteExistingTeam,
    fetchAvailableUsers,
    addMembersToTeam,
    removeMemberFromTeam,
    setSelectedTeam,
    clearError,
  } = useSuperAdminMyTeamStore();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchAllTeams();
  }, [fetchAllTeams]);

  useEffect(() => {
    if (error) {
      setTimeout(() => clearError(), 5000);
    }
  }, [error, clearError]);

  useEffect(() => {
    if (successMessage) {
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  }, [successMessage]);

  const handleCreateTeam = async () => {
    if (!teamName.trim()) return;

    const result = await createNewTeam(teamName, teamDescription);
    if (result.success) {
      setShowCreateModal(false);
      setTeamName('');
      setTeamDescription('');
      setSuccessMessage('Team created successfully!');
    }
  };

  const handleUpdateTeam = async () => {
    if (!selectedTeam || !teamName.trim()) return;

    const success = await updateExistingTeam(selectedTeam.id, teamName, teamDescription);
    if (success) {
      setShowEditModal(false);
      setTeamName('');
      setTeamDescription('');
      setSuccessMessage('Team updated successfully!');
    }
  };

  const handleDeleteTeam = async (teamId: number) => {
    if (window.confirm('Are you sure you want to delete this team?')) {
      const success = await deleteExistingTeam(teamId);
      if (success) {
        setSelectedTeam(null);
        setSuccessMessage('Team deleted successfully!');
      }
    }
  };

  const handleViewTeam = async (team: Team) => {
    await fetchTeamById(team.id);
  };

  const handleOpenAddMembers = async () => {
    await fetchAvailableUsers();
    setSelectedUserIds([]);
    setShowAddMembersModal(true);
  };

  const handleAddMembers = async () => {
    if (!selectedTeam || selectedUserIds.length === 0) return;

    const result = await addMembersToTeam(selectedTeam.id, selectedUserIds);
    if (result.success) {
      setShowAddMembersModal(false);
      setSelectedUserIds([]);
      setSuccessMessage(`${result.addedCount} member(s) added successfully!`);
      // Refresh team details
      await fetchTeamById(selectedTeam.id);
    }
  };

  const handleRemoveMember = async (userId: string, userName: string) => {
    if (!selectedTeam) return;

    if (window.confirm(`Remove ${userName} from this team?`)) {
      const success = await removeMemberFromTeam(selectedTeam.id, userId);
      if (success) {
        setSuccessMessage('Member removed successfully!');
        await fetchTeamById(selectedTeam.id);
      }
    }
  };

  const handleEditTeam = (team: Team) => {
    setTeamName(team.name);
    setTeamDescription(team.description || '');
    setShowEditModal(true);
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const filteredAvailableUsers = availableUsers?.filter(user =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="manage-teams-container">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="manage-teams-success-message">
          <CheckCircle size={16} />
          {successMessage}
        </div>
      )}
      {error && (
        <div className="manage-teams-error-message">
          {error}
        </div>
      )}

      <div className="manage-teams-layout">
        {/* Teams List Panel */}
        <div className="manage-teams-list-panel">
          <div className="manage-teams-list-header">
            <h3>Teams</h3>
            <button
              className="manage-teams-create-btn"
              onClick={() => {
                setTeamName('');
                setTeamDescription('');
                setShowCreateModal(true);
              }}
            >
              <Plus size={16} />
              Create Team
            </button>
          </div>

          <div className="manage-teams-list">
            {loading && !teams ? (
              <div className="manage-teams-loading">Loading teams...</div>
            ) : teams?.length === 0 ? (
              <div className="manage-teams-empty">
                <Users size={48} />
                <p>No teams created yet</p>
                <button
                  className="manage-teams-create-first-btn"
                  onClick={() => setShowCreateModal(true)}
                >
                  Create Your First Team
                </button>
              </div>
            ) : (
              teams?.map(team => (
                <div
                  key={team.id}
                  className={`manage-teams-item ${selectedTeam?.id === team.id ? 'active' : ''}`}
                  onClick={() => handleViewTeam(team)}
                >
                  <div className="manage-teams-item-icon">
                    <Users size={20} />
                  </div>
                  <div className="manage-teams-item-info">
                    <div className="manage-teams-item-name">{team.name}</div>
                    <div className="manage-teams-item-count">{team.memberCount} members</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Team Details Panel */}
        <div className="manage-teams-detail-panel">
          {selectedTeam ? (
            <>
              <div className="manage-teams-detail-header">
                <div className="manage-teams-detail-title">
                  <h2>{selectedTeam.name}</h2>
                  {selectedTeam.description && (
                    <p className="manage-teams-detail-desc">{selectedTeam.description}</p>
                  )}
                </div>
                <div className="manage-teams-detail-actions">
                  <button
                    className="manage-teams-action-btn edit"
                    onClick={() => handleEditTeam(selectedTeam)}
                    title="Edit Team"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    className="manage-teams-action-btn delete"
                    onClick={() => handleDeleteTeam(selectedTeam.id)}
                    title="Delete Team"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="manage-teams-members-section">
                <div className="manage-teams-members-header">
                  <h3>Team Members ({teamMembers?.length || 0})</h3>
                  <button
                    className="manage-teams-add-member-btn"
                    onClick={handleOpenAddMembers}
                  >
                    <UserPlus size={16} />
                    Add Members
                  </button>
                </div>

                <div className="manage-teams-members-list">
                  {teamMembers?.length === 0 ? (
                    <div className="manage-teams-no-members">
                      <p>No members in this team yet</p>
                      <button
                        className="manage-teams-add-first-btn"
                        onClick={handleOpenAddMembers}
                      >
                        Add Members
                      </button>
                    </div>
                  ) : (
                    teamMembers?.map(member => (
                      <div key={member.id} className="manage-teams-member-card">
                        <div className="manage-teams-member-avatar">{member.initials}</div>
                        <div className="manage-teams-member-info">
                          <div className="manage-teams-member-name">{member.name}</div>
                          <div className="manage-teams-member-email">{member.email}</div>
                          <div className="manage-teams-member-meta">
                            {member.jobTitle} • {member.department}
                          </div>
                        </div>
                        <div className="manage-teams-member-stats">
                          <div className="manage-teams-member-progress">
                            <span>{member.progress}%</span>
                            <div className="manage-teams-progress-bar">
                              <div
                                className="manage-teams-progress-fill"
                                style={{ width: `${member.progress}%` }}
                              />
                            </div>
                          </div>
                          <div className="manage-teams-member-courses">
                            {member.completedCourses} completed
                          </div>
                        </div>
                        <button
                          className="manage-teams-remove-btn"
                          onClick={() => handleRemoveMember(member.id, member.name)}
                          title="Remove from team"
                        >
                          <UserMinus size={16} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="manage-teams-no-selection">
              <Users size={64} />
              <h3>Select a Team</h3>
              <p>Choose a team from the list to view its members and details</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Team Modal */}
      {showCreateModal && (
        <div className="manage-teams-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="manage-teams-modal" onClick={e => e.stopPropagation()}>
            <div className="manage-teams-modal-header">
              <h3>Create New Team</h3>
              <button className="manage-teams-close-btn" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="manage-teams-modal-body">
              <div className="manage-teams-form-group">
                <label>Team Name *</label>
                <input
                  type="text"
                  value={teamName}
                  onChange={e => setTeamName(e.target.value)}
                  placeholder="Enter team name"
                  autoFocus
                />
              </div>
              <div className="manage-teams-form-group">
                <label>Description</label>
                <textarea
                  value={teamDescription}
                  onChange={e => setTeamDescription(e.target.value)}
                  placeholder="Enter team description (optional)"
                  rows={3}
                />
              </div>
            </div>
            <div className="manage-teams-modal-footer">
              <button
                className="manage-teams-cancel-btn"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
              <button
                className="manage-teams-submit-btn"
                onClick={handleCreateTeam}
                disabled={!teamName.trim() || loading}
              >
                {loading ? 'Creating...' : 'Create Team'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {showEditModal && (
        <div className="manage-teams-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="manage-teams-modal" onClick={e => e.stopPropagation()}>
            <div className="manage-teams-modal-header">
              <h3>Edit Team</h3>
              <button className="manage-teams-close-btn" onClick={() => setShowEditModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="manage-teams-modal-body">
              <div className="manage-teams-form-group">
                <label>Team Name *</label>
                <input
                  type="text"
                  value={teamName}
                  onChange={e => setTeamName(e.target.value)}
                  placeholder="Enter team name"
                  autoFocus
                />
              </div>
              <div className="manage-teams-form-group">
                <label>Description</label>
                <textarea
                  value={teamDescription}
                  onChange={e => setTeamDescription(e.target.value)}
                  placeholder="Enter team description (optional)"
                  rows={3}
                />
              </div>
            </div>
            <div className="manage-teams-modal-footer">
              <button
                className="manage-teams-cancel-btn"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button
                className="manage-teams-submit-btn"
                onClick={handleUpdateTeam}
                disabled={!teamName.trim() || loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Members Modal */}
      {showAddMembersModal && (
        <div className="manage-teams-modal-overlay" onClick={() => setShowAddMembersModal(false)}>
          <div className="manage-teams-modal manage-teams-modal-large" onClick={e => e.stopPropagation()}>
            <div className="manage-teams-modal-header">
              <h3>Add Members to {selectedTeam?.name}</h3>
              <button className="manage-teams-close-btn" onClick={() => setShowAddMembersModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="manage-teams-modal-body">
              <div className="manage-teams-search-box">
                <Search size={16} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search users by name or email..."
                />
              </div>

              {selectedUserIds.length > 0 && (
                <div className="manage-teams-selected-count">
                  {selectedUserIds.length} user(s) selected
                </div>
              )}

              <div className="manage-teams-users-list">
                {filteredAvailableUsers?.length === 0 ? (
                  <div className="manage-teams-no-users">
                    No available users found
                  </div>
                ) : (
                  filteredAvailableUsers?.map(user => (
                    <div
                      key={user.id}
                      className={`manage-teams-user-item ${selectedUserIds.includes(user.id) ? 'selected' : ''}`}
                      onClick={() => toggleUserSelection(user.id)}
                    >
                      <div className="manage-teams-user-checkbox">
                        {selectedUserIds.includes(user.id) && <CheckCircle size={18} />}
                      </div>
                      <div className="manage-teams-user-info">
                        <div className="manage-teams-user-name">{user.name}</div>
                        <div className="manage-teams-user-email">{user.email}</div>
                        <div className="manage-teams-user-meta">
                          {user.jobTitle} • {user.department}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="manage-teams-modal-footer">
              <button
                className="manage-teams-cancel-btn"
                onClick={() => setShowAddMembersModal(false)}
              >
                Cancel
              </button>
              <button
                className="manage-teams-submit-btn"
                onClick={handleAddMembers}
                disabled={selectedUserIds.length === 0 || loading}
              >
                {loading ? 'Adding...' : `Add ${selectedUserIds.length} Member(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTeamsTab;
